import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { deviceServiceLabel } from '~/components/devices';
import { useDeviceFilters } from '../context';

export const FilterChips = () => {
  const { filters, setFilter } = useDeviceFilters();
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {filters.arch.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`arch-${f.value}`}
            label={t('pages.devices.list.filters.arch_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.arch.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'arch', items });
            }}
          />
        ))}
      {filters.demo.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`demo-${f.value}`}
            label={t('common.labels.demo_with_value', {
              value: f.value ? t('common.boolean.true') : t('common.boolean.false'),
            })}
            color="primary"
            onDelete={() => {
              const items = filters.demo.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'demo', items });
            }}
          />
        ))}
      {filters.displayOn.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`display-${f.value}`}
            label={t('pages.devices.list.filters.display_with_value', {
              value: f.value
                ? t('pages.devices.show.details.display_on')
                : t('pages.devices.show.details.display_off'),
            })}
            color="primary"
            onDelete={() => {
              const items = filters.displayOn.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'displayOn', items });
            }}
          />
        ))}
      {filters.displayServers.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`display-server-${f.value}`}
            label={t('pages.devices.list.filters.display_server_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.displayServers.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'displayServers', items });
            }}
          />
        ))}
      {filters.groups.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`group-${f.value}`}
            label={t('common.labels.group_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.groups.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'groups', items });
            }}
          />
        ))}
      {filters.internal.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`internal-${f.value}`}
            label={t('common.labels.internal_with_value', {
              value: f.value ? t('common.boolean.true') : t('common.boolean.false'),
            })}
            color="primary"
            onDelete={() => {
              const items = filters.internal.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'internal', items });
            }}
          />
        ))}
      {filters.kind.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`kind-${f.value}`}
            label={t('common.labels.type_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.kind.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'kind', items });
            }}
          />
        ))}
      {filters.services.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            color="primary"
            key={`service-${f.value}`}
            label={t('pages.devices.list.filters.service_with_value', {
              value: deviceServiceLabel(t, f.value),
            })}
            onDelete={() => {
              const items = filters.services.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'services', items });
            }}
          />
        ))}
      {filters.silence.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`silence-${f.value}`}
            label={t('pages.devices.list.filters.silenced_with_value', {
              value: f.value ? t('common.boolean.true') : t('common.boolean.false'),
            })}
            color="primary"
            onDelete={() => {
              const items = filters.silence.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'silence', items });
            }}
          />
        ))}
      {filters.status.items
        .filter((f) => f.selected)
        .map((f) => {
          // Translate status values: Healthy -> Online, Error -> Offline, Warn -> Warning
          let statusLabel = f.label;
          if (f.value === 'HEALTHY') statusLabel = t('pages.devices.list.status_toggle.online');
          else if (f.value === 'ERROR') statusLabel = t('pages.devices.list.status_toggle.offline');
          else statusLabel = t('pages.devices.list.status_toggle.warning');

          return (
            <Chip
              key={`status-${f.value}`}
              label={t('common.labels.status_with_value', { value: statusLabel })}
              color="primary"
              onDelete={() => {
                const items = filters.status.items.map((x) =>
                  x.value === f.value ? { ...x, selected: false } : x,
                );
                setFilter({ field: 'status', items });
              }}
            />
          );
        })}
      {filters.tags.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`tag-${f.value}`}
            label={t('pages.devices.list.filters.tag_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.tags.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'tags', items });
            }}
          />
        ))}
      {filters.tvBrands.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`tvBrand-${f.value}`}
            label={t('common.labels.tv_brand_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.tvBrands.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'tvBrands', items });
            }}
          />
        ))}
      {filters.models.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`model-${f.value}`}
            label={t('pages.devices.list.filters.model_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.models.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'models', items });
            }}
          />
        ))}
      {filters.playerVersions.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`player-version-${f.value}`}
            label={t('pages.devices.list.filters.player_version_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.playerVersions.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'playerVersions', items });
            }}
          />
        ))}
      {filters.storageBelow.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`storage-below-${f.value}`}
            label={t('pages.devices.list.filters.storage_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.storageBelow.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'storageBelow', items });
            }}
          />
        ))}
      {filters.temperatureAbove.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`temperature-above-${f.value}`}
            label={t('pages.devices.list.filters.temperature_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.temperatureAbove.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'temperatureAbove', items });
            }}
          />
        ))}
      {filters.uptimeBelow.items
        .filter((f) => f.selected)
        .map((f) => (
          <Chip
            key={`uptime-below-${f.value}`}
            label={t('pages.devices.list.filters.uptime_with_value', { value: f.label })}
            color="primary"
            onDelete={() => {
              const items = filters.uptimeBelow.items.map((x) =>
                x.value === f.value ? { ...x, selected: false } : x,
              );
              setFilter({ field: 'uptimeBelow', items });
            }}
          />
        ))}
    </Box>
  );
};
